// src/App.js
import { useState } from 'react';
import './App.css';

function Square({ value, onClick, highlight }) {
  const classes = ['square'];
  if (highlight) classes.push('square-highlight');
  return (
    <button type="button" className={classes.join(' ')} onClick={onClick}>
      {value}
    </button>
  );
}

function Board({ squares, onSquareClick, winningLine }) {
  const size = 3;
  const rows = Array.from({ length: size }, (_, row) => {
    const cols = Array.from({ length: size }, (_, col) => {
      const index = row * size + col;
      const isHighlight = winningLine?.includes(index);
      return (
        <Square
          key={index}
          value={squares[index]}
          highlight={isHighlight}
          onClick={() => onSquareClick(index)}
        />
      );
    });
    return (
      <div key={row} className="board-row">
        {cols}
      </div>
    );
  });

  return <div className="board">{rows}</div>;
}

function Game() {
  const [history, setHistory] = useState([
    { squares: Array(9).fill(null), location: null },
  ]);
  const [currentMove, setCurrentMove] = useState(0);
  const [isAscending, setIsAscending] = useState(true);
  const currentSquares = history[currentMove].squares;
  const xIsNext = currentMove % 2 === 0;
  const { winner, line: winningLine } = calculateWinner(currentSquares);

  function handlePlay(nextSquares, squareIndex) {
    const row = Math.floor(squareIndex / 3) + 1;
    const col = (squareIndex % 3) + 1;
    const nextHistory = [
      ...history.slice(0, currentMove + 1),
      { squares: nextSquares, location: `(${row}, ${col})` },
    ];
    setHistory(nextHistory);
    setCurrentMove(nextHistory.length - 1);
  }

  function handleSquareClick(i) {
    const squares = currentSquares.slice();
    if (winner || squares[i]) return;
    squares[i] = xIsNext ? 'X' : 'O';
    handlePlay(squares, i);
  }

  function jumpTo(move) {
    setCurrentMove(move);
  }

  const moves = history.map((step, move) => {
    const location = step.location ?? '';
    const description = move ? `You are at move #${move}` : 'Game start';
    const isCurrent = move === currentMove;

    if (isCurrent) {
      const message = move
        ? `You are at move #${move} ${location}`.trim()
        : 'You are at move #0 (start)';
      return (
        <li key={move} className="move move-current">
          <div className="move-pill">
            <span className="move-step">{message}</span>
            <span className="move-badge">current</span>
          </div>
        </li>
      );
    }

    return (
      <li key={move} className="move">
        <button
          type="button"
          className="move-button"
          onClick={() => jumpTo(move)}
        >
          <span className="move-step">{description}</span>
          {location && <span className="move-location">{location}</span>}
        </button>
      </li>
    );
  });

  const orderedMoves = isAscending ? moves : [...moves].reverse();
  const noMovesLeft = currentSquares.every(Boolean);
  const statusMessage = winner
    ? `Winner: ${winner}`
    : noMovesLeft
    ? 'Result: Draw'
    : `Next player: ${xIsNext ? 'X' : 'O'}`;

  const helperText = winner
    ? 'Three in a row! The glowing tiles show the winning path.'
    : noMovesLeft
    ? 'No moves left -- hit "Restart game" to play again.'
    : `It's ${xIsNext ? 'X' : 'O'}'s turn. Tap any empty square to play.`;

  const statusClasses = ['status-pill'];
  if (winner) {
    statusClasses.push('status-pill-winner');
  } else if (noMovesLeft) {
    statusClasses.push('status-pill-draw');
  } else {
    statusClasses.push(xIsNext ? 'status-pill-x' : 'status-pill-o');
  }

  const totalMoves = currentSquares.filter(Boolean).length;
  const xCount = currentSquares.filter((value) => value === 'X').length;
  const oCount = currentSquares.filter((value) => value === 'O').length;

  return (
    <div className="app-shell">
      <div className="glass-card">
        <header className="game-header">
          <div>
            <p className="eyebrow">React practice</p>
            <h1>Tic Tac Toe Deluxe</h1>
          </div>
          <div className="status-card">
            <span className="status-label">Status</span>
            <span className={statusClasses.join(' ')}>{statusMessage}</span>
          </div>
        </header>

        <div className="stats-grid">
          <div className="stat-card">
            <span className="stat-label">Moves played</span>
            <span className="stat-value">{totalMoves}</span>
          </div>
          <div className="stat-card">
            <span className="stat-label">X tiles</span>
            <span className="stat-value stat-x">{xCount}</span>
          </div>
          <div className="stat-card">
            <span className="stat-label">O tiles</span>
            <span className="stat-value stat-o">{oCount}</span>
          </div>
        </div>

        <div className="game-layout">
          <div className="board-panel">
            <Board
              squares={currentSquares}
              onSquareClick={handleSquareClick}
              winningLine={winningLine}
            />
            <p className="helper-text">{helperText}</p>
          </div>

          <div className="side-panel">
            <div className="panel-actions">
              <button
                type="button"
                className="ghost-btn"
                onClick={() => setIsAscending((prev) => !prev)}
              >
                Sort moves:{' '}
                {isAscending ? 'oldest -> newest' : 'newest -> oldest'}
              </button>
              <button
                type="button"
                className="ghost-btn"
                onClick={() => jumpTo(0)}
              >
                Restart game
              </button>
            </div>
            <ol className="moves-list">{orderedMoves}</ol>
          </div>
        </div>
      </div>
    </div>
  );
}

function calculateWinner(squares) {
  const lines = [
    [0, 1, 2],
    [3, 4, 5],
    [6, 7, 8],
    [0, 3, 6],
    [1, 4, 7],
    [2, 5, 8],
    [0, 4, 8],
    [2, 4, 6],
  ];
  for (const [a, b, c] of lines) {
    if (squares[a] && squares[a] === squares[b] && squares[a] === squares[c]) {
      return { winner: squares[a], line: [a, b, c] };
    }
  }
  return { winner: null, line: null };
}

export default Game;
